import { Trans } from 'react-i18next';

import { Modal } from 'antd';
import type {
  ConvertToPdfResponse,
  CreateFileFromTemplatePayload,
  CreateFolderPayload,
  GetFilesPayload,
  GetFilesResponse,
  MoveFilesResponse,
} from 'interfaces/FileApiService';
import { useCallback } from 'react';
import { FileFromTemplateForm } from '../../../components/Forms/FileFromTemplateForm';
import { FileMoveForm } from '../../../components/Forms/FileMoveForm';
import { FilesMoveErrorForm } from '../../../components/Forms/FilesMoveErrorForm';
import FileSellForm from '../../../components/Forms/FileSellForm';
import { FileShortcutForm } from '../../../components/Forms/FileShortcutForm';
import { FileTagsForm } from '../../../components/Forms/FileTagsForm';
import { FileTitleForm } from '../../../components/Forms/FileTitleForm';
import { FilesTableEnum } from '../../../enums/FilesTableEnum';
import type File from '../../../interfaces/File';
import type { OpenFileLocallyPayload } from '../../../interfaces/FileApiService';
import { toAbsoluteServiceUrl } from '../../../lib/absoluteServiceUrl';
import { useAppStore } from '../../../store/store';
import { handleFilePasswordError } from '../../../utils/filePasswordErrors';
import type { UseFilesActionsDeps } from '../deps';
import { fileService } from '../services';
import type { UseFilesActionsEvents, UseFilesActionsReturn } from '../types';
import { ApiErrorEnum } from '../../../enums/ApiErrorEnum';
import { isApiError } from '../../../utils/apiErrorCode';
import { partitionFilesByMoveResult } from '../../../utils/filesMoveResult';
import { resolveConvertToPdfApiErrorCopy } from '../../../lib/pdfConversionErrors';
import { startPdfConversionPolling } from '../../../lib/pdfConversionPolling';
import { addPendingConversion } from '../../../lib/pdfConversionTracking';

/**
 * Non-destructive CRUD and folder UX: templates, rename, tags, shortcuts, move, visibility, offline, and read state.
 */
export function useFilesCrudActions(
  deps: UseFilesActionsDeps,
): Pick<
  UseFilesActionsReturn,
  | 'handleCreateFolder'
  | 'handleCreateFileFromTemplate'
  | 'handleRenameFile'
  | 'handleFavouriteFileChange'
  | 'handleCreateFileShortcut'
  | 'handleCreateFilesDuplicate'
  | 'handleReplaceFile'
  | 'handleMoveFiles'
  | 'handleConvertToPdf'
  | 'handleHideFiles'
  | 'handleUnhideFiles'
  | 'handleUpdateTags'
  | 'handleEditFileTags'
  | 'handleOpenShortcutOriginalFileLocation'
  | 'handleSellFile'
  | 'handleOpenFileLocally'
  | 'handleKeepFileOffline'
  | 'handleMarkFilesAsRead'
  | 'handleMarkFilesAsUnread'
  | 'moveFiles'
> {
  const {
    folderUidRef,
    t,
    modal,
    notification,
    message,
    params,
    authenticatedUser,
    isAdminOrContributor,
    uploadFiles,
    tags,
    redirectFileToLocation,
    token,
    refreshToken,
    decreaseReceivedUnreadCountBy,
    increaseReceivedUnreadCountBy,
  } = deps;

  /** Creates a child folder for the current parent; suppresses generic errors when password flows handle the failure. */
  const createFolder = useCallback(
    (title: string, events?: UseFilesActionsEvents) => {
      const payload: CreateFolderPayload = {
        name: title,
        parent: folderUidRef.current || undefined,
      };

      fileService
        .createFolder(payload)
        .then((response) => {
          const newFolder: File = {
            ...response,
            isUnRead: true,
          };
          events?.onFilesCreated?.([newFolder]);
          message.success({
            content: t('notifications.folderCreated', {
              resource: title,
            }),
          });
        })
        .catch((error) => {
          if (handleFilePasswordError(error)) {
            return;
          }

          notification.error({
            title: t('errors.genericCreateError', {
              resource: t('common.folder'),
            }),
          });
        })
        .finally(() => {});
    },
    [folderUidRef, t, notification],
  );

  /**
   * Create a new file from a template
   * @param title - The title of the file
   * @param template - The template to use
   */
  const createFileFromTemplate = useCallback(
    (title: string, template: 'word' | 'excel' | 'powerpoint', events?: UseFilesActionsEvents) => {
      const payload: CreateFileFromTemplatePayload = {
        name: title,
        parent: folderUidRef.current ? parseInt(folderUidRef.current) : undefined,
        template: template,
      };

      fileService
        .createFileFromTemplate(payload)
        .then((response) => {
          const newFile: File = {
            ...response,
            isUnRead: true,
          };
          events?.onFilesCreated?.([newFile]);
          message.success({
            content: t('notifications.fileCreated', {
              resource: title,
            }),
          });
        })
        .catch(() => {
          notification.error({
            title: t('errors.genericCreateError', {
              resource: t('common.file'),
            }),
          });
        })
        .finally(() => {});
    },
    [folderUidRef, t, notification],
  );

  /**
   * Rename a file
   */
  const renameFile = useCallback(
    (file: File, newName: string, events?: UseFilesActionsEvents) => {
      fileService
        .renameFile(file.cufi, newName)
        .then(() => {
          const updatedFile: File = {
            ...file,
            fileName: newName,
          };
          events?.onFilesUpdated?.([updatedFile]);
          message.success({
            content: t('notifications.fileRenamed', {
              resource: file.fileName,
              newName: newName,
            }),
          });
        })

        .catch(() => {
          notification.error({
            title: t('errors.genericUpdateError', {
              resource: t('common.file'),
            }),
          });
        })
        .finally(() => {});
    },
    [fileService, t, notification],
  );

  /**
   * Create a new file duplicate
   * @param file - The file to create the duplicate of
   */
  const createFileDuplicate = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      fileService
        .duplicateFiles(
          files.map((f) => f.cufi),
          t('common.copyOf'),
        )
        .then((response: GetFilesResponse) => {
          const responseFiles = response?.myFiles;

          const newFiles = responseFiles?.map((f) => ({
            ...f,
            isUnRead: true,
          }));

          events?.onFilesCreated?.(newFiles);
          message.success({
            content: t('notifications.fileDuplicated', {
              resource: files[0]?.fileName || '',
              count: newFiles?.length || 0,
            }),
          });
        })
        .catch(() => {
          notification.error({
            title: t('errors.genericCreateError', {
              resource: t('common.file'),
            }),
          });
        })
        .finally(() => {});
    },
    [fileService, t, notification],
  );

  /**
   * Change the favourite status of a file
   * @param file - The file to change the favourite status of
   * @param isFavourite - Whether the file is favourite
   */
  const changeFavouriteFile = useCallback(
    (files: File[], isFavourite: boolean, events?: UseFilesActionsEvents) => {
      fileService
        .updateFavouriteFiles(
          files.map((f) => f.cufi),
          isFavourite,
        )
        .then(() => {
          const updatedFiles: File[] = files.map((f) => ({
            ...f,
            isFavourite: isFavourite,
          }));
          events?.onFilesUpdated?.(updatedFiles);
          message.success({
            content: t('notifications.fileFavouriteChanged', {
              resource: files[0]?.fileName || '',
              isFavourite: isFavourite
                ? t('common.favouriteAddedTo')
                : t('common.favouriteRemovedFrom'),
              count: updatedFiles?.length || 0,
            }),
          });
        })
        .catch((error) => {
          if (error?.status === 409) {
            notification.error({
              title: t('errors.cannotAddArchivedFilesToFavorites'),
            });
          } else {
            notification.error({
              title: t('errors.genericUpdateError', {
                resource: t('common.file'),
              }),
            });
          }
        })
        .finally(() => {});
    },
    [fileService, t, notification],
  );

  /**
   * Create a new file shortcut
   * @param file - The file to create the shortcut of
   * @param folderUid - The folder UID where to create the shortcut
   * @param targetListType - The list type where the shortcut was created (personal or received)
   * @param events - Optional events callbacks
   */
  const createFileShortcut = useCallback(
    (
      file: File,
      folderUid: number | null,
      targetListType?: 'personal' | 'received' | 'favorites',
      events?: UseFilesActionsEvents,
    ) => {
      fileService
        .createShortcut(file.cufi, folderUid?.toString() || undefined)
        .then((response) => {
          const newFile: File = {
            ...response,
            isUnRead: true,
          };

          events?.onFilesCreated?.([newFile]);

          // Get current folderUid from route params
          const currentFolderUid = params?.folderUid;
          // Convert destination folderUid to string for comparison
          const destinationFolderUid = folderUid?.toString();

          // Only redirect if we're in a different folder
          // If both are null/undefined, we're both in root, so don't redirect
          // If they're different, redirect
          const shouldRedirect =
            currentFolderUid !== destinationFolderUid &&
            !(currentFolderUid === undefined && destinationFolderUid === undefined);

          if (shouldRedirect) {
            redirectFileToLocation({
              file: newFile,
              avoidOpenViewer: true,
              targetTable:
                targetListType === 'received'
                  ? FilesTableEnum.Received
                  : targetListType === 'favorites'
                    ? FilesTableEnum.Favorites
                    : FilesTableEnum.Personal,
              queryParams: {
                prependFileId: response.id.toString(),
              },
            });
          }

          message.success({
            content: t('notifications.fileShortcutCreated', {
              resource: file.fileName,
            }),
          });
        })
        .catch(() => {
          notification.error({
            title: t('errors.genericUpdateError', {
              resource: t('common.file'),
            }),
          });
        })
        .finally(() => {});
    },
    [fileService, t, notification, params?.folderUid, redirectFileToLocation, FilesTableEnum],
  );

  /**
   * Replace a file
   * @param file - The file to replace
   */
  const replaceFile = useCallback(
    (file: File) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = file.fileExtension;
      input.onchange = () => {
        const selectedFiles = input.files;
        if (selectedFiles) {
          uploadFiles(
            selectedFiles,
            false,
            folderUidRef.current ? parseInt(folderUidRef?.current) : undefined,
            file.cufi,
          );
        }
      };
      input.click();
    },
    [folderUidRef, uploadFiles],
  );

  /**
   * Move files
   * @param folder - The folder to move the files to
   * @param filesToMove - The files to move
   */
  const openMoveExcludedFilesModal = useCallback(
    (failedFilesList: NonNullable<MoveFilesResponse['failedFilesList']>) => {
      modal.warning({
        title: t('common.excludedFilesTitle'),
        footer: null,
        content: (
          <FilesMoveErrorForm
            failedFilesList={failedFilesList}
            onClose={() => Modal.destroyAll()}
          />
        ),
      });
    },
    [modal, t],
  );

  const moveFiles = useCallback(
    (folderUid: number | null, filesToMove: File[], events?: UseFilesActionsEvents) => {
      if (filesToMove?.length === 0) {
        return;
      }

      const fileIds = filesToMove.map((file) => file?.cufi);

      if (fileIds.length === 0) {
        return;
      }

      fileService
        .moveFiles(folderUid, fileIds)
        .then((response) => {
          const { movedFiles, failedFiles } = partitionFilesByMoveResult(
            filesToMove,
            response?.failedFilesList,
          );

          if (movedFiles.length > 0) {
            events?.onFilesDeleted?.(movedFiles);
            events?.onFilesUpdated?.(movedFiles);
          }

          if (failedFiles.length === 0) {
            message.success({
              content: t('notifications.fileMoved', {
                resource: filesToMove[0]?.fileName || '',
                newLocation: folderUid?.toString() || '',
              }),
            });
            return;
          }

          if (movedFiles.length > 0) {
            notification.warning({
              message: t('common.moveCompletedWithExclusions'),
              placement: 'top',
            });
          } else {
            notification.error({
              title: t('errors.errorMoveFile'),
            });
          }

          openMoveExcludedFilesModal(failedFiles);
        })
        .catch(() => {
          notification.error({
            title: t('errors.errorMoveFile'),
          });
        });
    },
    [fileService, t, notification, message, openMoveExcludedFilesModal],
  );

  /**
   * Hide files
   * @param files - The files to hide
   */
  const hideFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      fileService
        .updateArchivedFiles(
          files.map((f) => f.cufi),
          true,
        )
        .then(() => {
          events?.onFilesDeleted?.(files);
          message.success({
            content: t('notifications.fileHidden', {
              resource: files[0]?.fileName || '',
              count: files?.length || 0,
            }),
          });
        })
        .catch(() => {
          notification.error({
            title: t('errors.errorHideFiles'),
          });
        })
        .finally(() => {});
    },
    [fileService, t, notification],
  );

  /**
   * Unhide files
   * @param files - The files to unhide
   */
  const unhideFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      fileService
        .updateArchivedFiles(
          files.map((f) => f.cufi),
          false,
        )
        .then(() => {
          events?.onFilesDeleted?.(files);
          message.success({
            content: t('notifications.fileUnhidden', {
              resource: files[0]?.fileName || '',
              count: files?.length || 0,
            }),
          });
        })
        .catch(() => {
          notification.error({
            title: t('errors.errorUnhideFiles'),
          });
        })
        .finally(() => {});
    },
    [fileService, t, notification],
  );
  const updateTags = useCallback(
    (file: File, tagIds: number[], events?: UseFilesActionsEvents) => {
      fileService
        .updateFileTags(file.id, tagIds)
        .then((response) => {
          // Preserve submit order (store filter order is unrelated / often alpha).
          const responseById = new Map((response.tags ?? []).map((t) => [t.id, t]));
          const updatedFile = {
            ...file,
            fileLimitations: response.fileLimitations,
            tags: tagIds.map((id) => {
              const fromResponse = responseById.get(id);
              return {
                id,
                readOnly: fromResponse?.readOnly ?? false,
              };
            }),
          };

          events?.onFilesUpdated?.([updatedFile]);
          message.success({
            content: t('notifications.fileTagsUpdated', {
              resource: file.fileName,
            }),
          });
        })
        .catch((error) => {
          if (isApiError(error.response, ApiErrorEnum.MandatoryClassificationCannotRemove)) {
            notification.error({
              title: t('errors.mandatoryClassificationCannotRemove'),
            });
          } else if (isApiError(error.response, ApiErrorEnum.TagRemovalDenied)) {
            notification.error({
              title: t('errors.tagRemovalDenied'),
            });
          } else if (isApiError(error.response, ApiErrorEnum.TagAdditionDenied)) {
            notification.error({
              title: t('errors.tagAdditionDenied'),
            });
          } else if (error.status === 404) {
            events?.onFilesDeleted?.([file]);
            useAppStore.getState().events.emit.onFilesDeleted({ files: [file] });
            message.success({
              content: t('notifications.fileTagsUpdated', {
                resource: file.fileName,
              }),
            });
          } else {
            notification.error({
              title: t('errors.genericUpdateError', {
                resource: t('common.tags'),
              }),
            });
          }
        })
        .finally(() => {});
    },
    [fileService, t, notification, tags],
  );

  /**
   * Fetches shortcut metadata and navigates to the original parent folder without opening the viewer.
   */
  const openShortcutOriginalFileLocation = useCallback(
    (file: File, _?: UseFilesActionsEvents) => {
      fileService
        .getFile(file.id)
        .then(async (fileDetail) => {
          const updatedFile = {
            ...file,
            parentId: fileDetail.originalShortcutParentId,
          };

          redirectFileToLocation({
            file: updatedFile,
            avoidOpenViewer: true,
            queryParams: {
              prependFileId: file.id.toString(),
            },
          });
        })
        .catch(() => {
          notification.error({
            title: t('errors.genericFetchError', {
              resource: t('common.file'),
            }),
          });
        })
        .finally(() => {});
    },
    [fileService, t, notification, redirectFileToLocation],
  );

  /** Invokes the desktop helper to open the file locally using embedded auth tokens. */
  const openFileLocally = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      const payload: OpenFileLocallyPayload = {
        webApp: window.location.origin,
        server: toAbsoluteServiceUrl(import.meta.env.VITE_APP_API_BASE_URL),
        fileId: file.id,
        token: token || '',
        refreshToken: refreshToken || '',
      };
      fileService
        .openFileLocally(payload)
        .then(() => {
          events?.onFilesUpdated?.([file]);
        })
        .catch(() => {
          Modal.warning({
            title: t('modal.openFileLocallyError.title'),
            content: t('modal.openFileLocallyError.description'),
            centered: true,
          });
        });
    },
    [fileService, t, token, refreshToken],
  );

  /** Marks the given files as read, updates unread counters, and emits a local read event for other views. */
  const markFilesAsRead = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      if (files.length === 0 || !files.some((f) => f.isUnRead)) return;

      fileService
        .markFilesAsRead(files.map((f) => f.id))
        .then((res) => {
          decreaseReceivedUnreadCountBy(res.updated);
          const updated = files.map((f) => ({ ...f, isUnRead: false as const }));
          events?.onFilesUpdated?.(updated);
          useAppStore.getState().events.emit.filesMarkedAsReadLocally({
            fileIds: files.map((f) => f.id),
          });
          message.success({
            content: t('notifications.filesMarkedAsRead', { count: files.length }),
          });
        })
        .catch(() => {
          notification.error({
            title: t('errors.genericUpdateError', {
              resource: t('common.files'),
            }),
          });
        });
    },
    [fileService, t, notification, message, decreaseReceivedUnreadCountBy],
  );

  /** Marks the given files as unread and bumps unread counters after a successful API call. */
  const markFilesAsUnread = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      if (files.length === 0 || !files.every((f) => !f.isUnRead)) return;

      fileService
        .markFilesAsUnread(files.map((f) => f.id))
        .then((res) => {
          increaseReceivedUnreadCountBy(res.updated);
          const updated = files.map((f) => ({ ...f, isUnRead: true as const }));
          events?.onFilesUpdated?.(updated);
          message.success({
            content: t('notifications.filesMarkedAsUnread', { count: files.length }),
          });
        })
        .catch(() => {
          notification.error({
            title: t('errors.genericUpdateError', {
              resource: t('common.files'),
            }),
          });
        });
    },
    [fileService, t, notification, message, increaseReceivedUnreadCountBy],
  );

  /** Registers selected files for offline availability via the local helper service. */
  const keepFileOffline = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      const payload = {
        webApp: window.location.origin,
        server: toAbsoluteServiceUrl(import.meta.env.VITE_APP_API_BASE_URL),
        fileIds: files.map((f) => f.id),
        token: token || '',
        refreshToken: refreshToken || '',
      };
      fileService
        .keepFilesOffline(payload)
        .then(() => {
          events?.onFilesUpdated?.(files);
        })
        .catch(() => {
          Modal.warning({
            title: t('modal.keepFilesOfflineError.title'),
            content: t('modal.keepFilesOfflineError.description'),
            centered: true,
          });
        });
    },
    [fileService, t, token, refreshToken],
  );
  const handleCreateFolder = useCallback(
    (events?: UseFilesActionsEvents) => {
      const modalInstance = modal.confirm({
        title: t('modal.fileTitleForm.newFolderTitle'),
        content: (
          <FileTitleForm
            isFolder={true}
            onSubmit={(title) => {
              createFolder(title, events);
              modalInstance.destroy();
            }}
            onCancel={() => {
              modalInstance.destroy();
            }}
          />
        ),
        footer: null,
      });
    },
    [modal, t, notification, authenticatedUser, moveFiles],
  );

  /**
   * Open the modal to create a new file from a template
   * @param template - The template to use
   */
  const handleCreateFileFromTemplate = useCallback(
    (template: 'word' | 'excel' | 'powerpoint', events?: UseFilesActionsEvents) => {
      if (!isAdminOrContributor) {
        notification.error({
          title: t('errors.noUploadPermission'),
        });
        return;
      }

      let title = '';

      switch (template) {
        case 'word':
          title = t('modal.fileFromTemplateForm.newWordDocumentTitle');
          break;
        case 'excel':
          title = t('modal.fileFromTemplateForm.newExcelDocumentTitle');
          break;
        case 'powerpoint':
          title = t('modal.fileFromTemplateForm.newPowerpointDocumentTitle');
          break;
        default:
          title = t('modal.fileFromTemplateForm.newGenericDocumentTitle');
      }

      const modalInstance = modal.confirm({
        title,
        content: (
          <FileFromTemplateForm
            template={template}
            onSubmit={(title) => {
              createFileFromTemplate(title, template, events);
              modalInstance.destroy();
            }}
            onCancel={() => {
              modalInstance.destroy();
            }}
          />
        ),
        footer: null,
      });
    },
    [modal, t, notification],
  );

  /**
   * Open the modal to rename a file
   */
  const handleRenameFile = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      const modalInstance = modal.confirm({
        title: file.isFolder
          ? t('modal.fileTitleForm.editFolderTitle')
          : t('modal.fileTitleForm.editFileTitle'),
        content: (
          <FileTitleForm
            file={file}
            onSubmit={(title) => {
              renameFile(file, title, events);
              modalInstance.destroy();
            }}
            onCancel={() => {
              modalInstance.destroy();
            }}
          />
        ),
        okText: t('common.rename'),
        footer: null,
      });
    },
    [modal, t, notification],
  );

  /**
   * Change the favourite status of a file
   * @param file - The file to change the favourite status of
   * @param isFavourite - Whether the file is favourite
   */
  const handleFavouriteFileChange = useCallback(
    (files: File[], isFavourite: boolean, events?: UseFilesActionsEvents) => {
      changeFavouriteFile(files, isFavourite, events);
    },
    [changeFavouriteFile],
  );

  /**
   * Create a new file shortcut
   * @param file - The file to create the shortcut of
   */
  const handleCreateFileShortcut = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      const modalInstance = modal.confirm({
        title: t('modal.fileShortcutForm.title', {
          resource: file?.fileName,
        }),
        content: (
          <FileShortcutForm
            selectedFile={file}
            currentFolder={null}
            onSubmit={(folderUid, listType) => {
              createFileShortcut(file, folderUid, listType, events);
              modalInstance.destroy();
            }}
            onCancel={() => {
              modalInstance.destroy();
            }}
          />
        ),
        footer: null,
      });
    },
    [createFileShortcut],
  );

  /**
   * Create a new file duplicate
   * @param file - The file to create the duplicate of
   */
  const handleCreateFilesDuplicate = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      createFileDuplicate(files, events);
    },
    [createFileDuplicate],
  );

  /**
   * Replace a file
   * @param file - The file to replace
   */
  const handleReplaceFile = useCallback(
    (file: File, _?: UseFilesActionsEvents) => {
      replaceFile(file);
    },
    [replaceFile],
  );

  /**
   * Open the modal to move files
   * @param files - The files to move
   * @param parentFolder - The parent folder
   * @param externalFilters - The external filters
   */
  const handleMoveFiles = useCallback(
    (
      files: File[],
      parentFolder?: File | null,
      externalFilters?: Partial<GetFilesPayload>,
      events?: UseFilesActionsEvents,
    ) => {
      const modalInstance = modal.confirm({
        title: t('modal.fileMoveForm.title', {
          resource: files?.[0]?.fileName,
          count: files.length,
        }),
        content: (
          <FileMoveForm
            selectedFiles={files}
            currentFolder={parentFolder}
            externalFilters={externalFilters}
            authenticatedUser={authenticatedUser ?? null}
            onMoveComplete={(destinationFolderId, movedFiles, failedFiles) => {
              if (movedFiles.length > 0) {
                events?.onFilesDeleted?.(movedFiles);
                events?.onFilesUpdated?.(movedFiles);
              }

              if (failedFiles.length === 0) {
                message.success({
                  content: t('notifications.fileMoved', {
                    resource: files?.[0]?.fileName || '',
                    newLocation: destinationFolderId?.toString() || '',
                  }),
                });
              } else if (movedFiles.length > 0) {
                notification.warning({
                  message: t('common.moveCompletedWithExclusions'),
                  placement: 'top',
                });
              }

              modalInstance.destroy();
            }}
            onCancel={() => {
              modalInstance.destroy();
            }}
          />
        ),
        footer: null,
      });
    },
    [modal, t, notification, message, authenticatedUser],
  );
  const handleConvertToPdf = useCallback(
    (
      file: File,
      parentFolder?: File | null,
      externalFilters?: Partial<GetFilesPayload>,
      _events?: UseFilesActionsEvents,
    ) => {
      const modalInstance = modal.confirm({
        title: t('modal.convertToPdf.title'),
        content: (
          <FileMoveForm
            selectedFiles={[file]}
            currentFolder={parentFolder}
            externalFilters={externalFilters}
            authenticatedUser={authenticatedUser ?? null}
            action="convertToPdf"
            onConvertConfirm={(destinationFolderId) => {
              modalInstance.destroy();

              // Call the convert API
              fileService
                .convertToPdf(file.id, destinationFolderId)
                .then((response: ConvertToPdfResponse) => {
                  const { processId } = response;

                  // Store the pending conversion in memory (destination for toast / list refresh)
                  addPendingConversion(processId, file.id, destinationFolderId, file.fileName);
                  startPdfConversionPolling(processId);

                  message.success({
                    content: t('notifications.pdfConversionStarted', {
                      resource: file.fileName,
                    }),
                  });
                })
                .catch((error: unknown) => {
                  if (handleFilePasswordError(error)) {
                    return;
                  }

                  const err = error as { response?: { data?: { maxSizeMb?: number } } };
                  const apiErrorCode = Object.values(ApiErrorEnum).find((code) =>
                    isApiError(err.response, code),
                  );
                  const mapped = resolveConvertToPdfApiErrorCopy(
                    apiErrorCode,
                    err.response?.data?.maxSizeMb,
                  );

                  if (mapped) {
                    notification.error({
                      title: t(mapped.titleKey, mapped.params),
                    });
                  } else {
                    notification.error({
                      title: t('errors.pdfConversionFailed'),
                    });
                  }
                });
            }}
            onMoveComplete={() => {
              // Not used in convert mode
            }}
            onCancel={() => {
              modalInstance.destroy();
            }}
          />
        ),
        footer: null,
      });
    },
    [modal, t, notification, message, authenticatedUser],
  );

  const handleHideFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      hideFiles(files, events);
    },
    [hideFiles],
  );

  /** Thin wrapper that restores previously hidden files through the API-backed `unhideFiles` helper. */
  const handleUnhideFiles = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      unhideFiles(files, events);
    },
    [unhideFiles],
  );

  /**
   * Propagates the post-submit file state from the sell form so list consumers can refresh without a refetch.
   */
  const sellFile = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      const updatedFile: File = {
        ...file,
      };

      events?.onFilesUpdated?.([updatedFile]);
    },
    [modal, t],
  );

  /**
   * Notifies listeners after a file is removed from the marketplace store (form-driven follow-up).
   */
  const removeFileFromStore = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      const updatedFile: File = {
        ...file,
      };

      events?.onFilesUpdated?.([updatedFile]);
    },
    [modal, t],
  );

  /** Persists tag IDs for a file (no modal); used by direct callers and flows that already collected tags. */
  const handleUpdateTags = useCallback(
    (file: File, tagIds: number[], events?: UseFilesActionsEvents) => {
      updateTags(file, tagIds, events);
    },
    [updateTags],
  );

  /**
   * Open the modal to edit file tags
   * @param file - The file to edit tags for
   */
  const handleEditFileTags = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      const modalInstance = modal.confirm({
        title: t('modal.fileTagsForm.title', {
          resource: file?.fileName,
        }),
        width: 600,
        content: (
          <div className="mt-4">
            <FileTagsForm
              file={file}
              onSubmit={(tagIds) => {
                updateTags(file, tagIds, events);
                modalInstance.destroy();
              }}
              onCancel={() => {
                modalInstance.destroy();
              }}
            />
          </div>
        ),
        footer: null,
      });
    },
    [modal, t, updateTags],
  );

  /** Navigates to the shortcut target folder for the given shortcut file. */
  const handleOpenShortcutOriginalFileLocation = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      openShortcutOriginalFileLocation(file, events);
    },
    [openShortcutOriginalFileLocation],
  );

  /**
   * Open the modal to sell a file
   * @param file - The file to sell
   */
  const handleSellFile = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      const modalInstance = modal.confirm({
        title: t('modal.fileSellForm.title', {
          resource: file?.fileName,
        }),
        width: 600,
        content: (
          <>
            <Trans>
              {t('modal.fileSellForm.message', {
                resource: file?.fileName,
              })}
            </Trans>

            <FileSellForm
              file={file}
              authenticatedUser={authenticatedUser ?? null}
              onSubmit={(file) => {
                sellFile(file, events);
              }}
              onCancel={() => {
                modalInstance.destroy();
              }}
              onClose={() => {
                modalInstance.destroy();
              }}
              onRemoveFromStore={(file) => {
                removeFileFromStore(file);
                modalInstance.destroy();
              }}
            />
          </>
        ),
        footer: null,
        okText: t('common.sell'),
      });
    },
    [fileService, t, notification],
  );
  const handleOpenFileLocally = useCallback(
    (file: File, events?: UseFilesActionsEvents) => {
      openFileLocally(file, events);
    },
    [openFileLocally],
  );

  /**
   * Keep the file offline
   * @param file - The file to keep offline
   */
  const handleKeepFileOffline = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      keepFileOffline(files, events);
    },
    [keepFileOffline],
  );

  const handleMarkFilesAsRead = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      markFilesAsRead(files, events);
    },
    [markFilesAsRead],
  );

  const handleMarkFilesAsUnread = useCallback(
    (files: File[], events?: UseFilesActionsEvents) => {
      markFilesAsUnread(files, events);
    },
    [markFilesAsUnread],
  );

  return {
    handleCreateFolder,
    handleCreateFileFromTemplate,
    handleRenameFile,
    handleFavouriteFileChange,
    handleCreateFileShortcut,
    handleCreateFilesDuplicate,
    handleReplaceFile,
    handleMoveFiles,
    handleConvertToPdf,
    handleHideFiles,
    handleUnhideFiles,
    handleUpdateTags,
    handleEditFileTags,
    handleOpenShortcutOriginalFileLocation,
    handleSellFile,
    handleOpenFileLocally,
    handleKeepFileOffline,
    handleMarkFilesAsRead,
    handleMarkFilesAsUnread,
    moveFiles,
  };
}
